export const BytesPerMegaByte = 1024 * 1024;

export const UploadUsage = {
  Image: 'image',
  Attachment: 'attachment',
  CsvImport: 'csvimport',
} as const;

export type UploadUsageType = (typeof UploadUsage)[keyof typeof UploadUsage];

export const isUploadUsageType = (value: unknown): value is UploadUsageType =>
  Object.values(UploadUsage).includes(value as UploadUsageType);

export const UploadMaxFileSizes: Record<UploadUsageType, number> = {
  image: 3 * BytesPerMegaByte, // 3MiB
  attachment: 10 * BytesPerMegaByte, // 10MiB
  csvimport: 250 * BytesPerMegaByte, // 250MiB
};

export const MAX_ATTACHMENT_FILES_PER_SELECTION = 10;

/**
 * The `error` fim sets on a 400 whose message is an attachment validator's reason. Only those
 * messages name the rejected file and are safe to show the uploader; any other failure message
 * is generic.
 */
export const AttachmentRejectedError = 'AttachmentRejected';

/**
 * The extensions an attachment picker offers, for both the `accept` attribute and client-side
 * validation.
 *
 * Lives beside the size limits rather than in a component library because the MUI attachments
 * component, the SUI one and the add-evidence dialog all need the same answer, and they import
 * from three different places. A per-library copy is what let the add-evidence dialog keep
 * offering only the five legacy image types after this set was expanded.
 *
 * `.xls` is omitted: file-type only sees a generic OLE2/CFB container, which also matches
 * `.doc`/`.ppt`/`.msi`. `.mp4` is held to the ordinary attachment limit on purpose: it is meant for
 * short snippets, not full recordings.
 */
export const DEFAULT_ACCEPTED_ATTACHMENT_TYPES = [
  '.svg',
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
  '.mp4',
  '.pdf',
  '.docx',
  '.xlsx',
  '.pptx',
  '.txt',
  '.csv',
  '.md',
  '.rtf',
];

export const EXTENSION_TO_MIME_TYPES: Record<string, readonly string[]> = {
  '.svg': ['image/svg+xml'],
  '.png': ['image/png', 'image/x-png'],
  '.jpg': ['image/jpeg', 'image/jpg', 'image/pjpeg'],
  '.jpeg': ['image/jpeg', 'image/jpg', 'image/pjpeg'],
  '.gif': ['image/gif'],
  '.webp': ['image/webp'],
  '.pdf': ['application/pdf'],
  // Windows reports .csv as an Excel type when Excel owns the file association.
  '.csv': ['text/csv', 'application/csv', 'application/vnd.ms-excel', 'text/plain'],
  '.docx': [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
  ],
  '.xlsx': [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ],
  '.pptx': [
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-powerpoint',
  ],
  '.txt': ['text/plain'],
  // Platforms without a Markdown association fall back to text/plain.
  '.md': ['text/markdown', 'text/x-markdown', 'text/plain'],
  // Windows registers .rtf as application/msword when Word owns the association.
  '.rtf': ['application/rtf', 'text/rtf', 'application/msword'],
  '.mp4': ['video/mp4'],
};

export type AttachmentFileLike = {
  name: string;
  type: string;
};

export const getFileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot <= 0) return '';
  return fileName.slice(lastDot).toLowerCase();
};

export const isAcceptedFileType = (
  file: AttachmentFileLike,
  acceptedFileTypes: readonly string[],
): boolean => {
  const extension = getFileExtension(file.name);
  if (!extension || !acceptedFileTypes.includes(extension)) return false;

  const allowedMimeTypes = EXTENSION_TO_MIME_TYPES[extension];
  if (file.type && allowedMimeTypes && !allowedMimeTypes.includes(file.type)) return false;

  return true;
};
